import { MaybeNil } from '../../../utils/interfaces';
import {
  ICohortRow,
  IPopulateFunnelBuilder,
  IStep,
  populateReportData,
} from '../../../redux/slices/oldBuilder';
import {
  BucketDefType,
  BucketKind,
  DateKindKey,
  DatePickerType,
  EventKind,
  ExpressionModeKey,
  FilterType,
  FunnelModeKey,
  FunnelOrderKey,
  IBasicBucketDefFieldsFragment,
  IBasicBucketDefInput,
  IBasicEventValueFieldsFragment,
  IBasicFieldValueFieldsFragment,
  IBreakdownFieldsFragment,
  IBucketDefFieldsFragment,
  IBucketDefInput,
  IBucketFieldsFragment,
  ICohortRefFieldsFragment,
  IConditionFieldsFragment,
  IDateBucketFieldsFragment,
  IDatePickerFieldsFragment,
  IDatePickerInput,
  IEnumBucketFieldsFragment,
  IEnumBucketInput,
  IEventConditionFieldsFragment,
  IEventConditionInput,
  IEventInput,
  IExpressionQueryFieldsFragment,
  IFilterFieldsFragment,
  ILogicalOperator,
  INamedBucketDefFieldsFragment,
  InputMaybe,
  IPathQueryFieldsFragment,
  IRangeBucketFieldsFragment,
  IRangeBucketInput,
  IRelationalOperatorFieldsFragment,
  ISliceNameHint,
  ISliceUserAliasFieldsFragment,
  Maybe,
  PathDirectionKey,
  QueryType,
  RelationalOperatorKey,
  TimeUnit,
} from '../../../utils/apollo/graphqlSchema';
import { isNil } from 'lodash';
import { defaultEventInput } from '../../../utils/builder';
import {
  IBasicBinning,
  IBinning,
  IDateBinning,
  IEnumBinning,
  INamedBinning,
  IRangeBinning,
} from '../../../redux/slices/oldBuilder/binning/types';
import { AppDispatch } from '../../../redux/store';
import produce from 'immer';
import {
  assertCondition,
  assertNotNil,
  byTypeName,
  isNotNil,
} from '../../../utils/types';
import { freeze } from '@reduxjs/toolkit';
import {
  MAX_BREAKDOWN_COHORTS,
  MAX_GLOBAL_COHORTS,
} from '../../../config/constants';
import { getExpandedRangeBuckets } from '../../../utils/binnings';
import { parseCohortRefToBuilder } from '../../../utils/builder/cohorts';
import { IPathAnalysis } from '../../../utils/apollo/responseTypes';
import { parseFilterToInput } from '../../../utils/filterFields';
import {
  IBuilderBreakdown,
  IBuilderBreakdownGroup,
} from '../../../redux/slices/oldBuilder/breakdown/types';
import { defaultBreakdown } from '../../../redux/slices/oldBuilder/breakdown/defaults';
import getBreakdownGroupLimit from '../../../utils/builder/getBreakdownGroupLimit';
import {
  defaultReportFilters,
  defaultStep,
} from '../../../redux/slices/oldBuilder/defaults';
import { UnreachableCaseError } from 'ts-essentials';

const parseLimitedCohortRefsToBuilder = (
  cohortRefs: ICohortRefFieldsFragment[],
  limit: number,
): ICohortRow[] => {
  if (!cohortRefs?.length) {
    return [];
  }

  if (cohortRefs.length > limit) {
    return cohortRefs.slice(0, limit).map(parseCohortRefToBuilder);
  }

  return cohortRefs.map(parseCohortRefToBuilder);
};

const parseGlobalCohortsToStatePayload = (
  cohortRefs: ICohortRefFieldsFragment[],
) => {
  return parseLimitedCohortRefsToBuilder(cohortRefs, MAX_GLOBAL_COHORTS);
};

const parseBreakdownGroupbys = (
  groupbys: IBreakdownFieldsFragment['groupbys'] | undefined,
  queryType: QueryType,
): IBuilderBreakdownGroup[] => {
  if (!groupbys?.length) {
    return [];
  }

  const limit = getBreakdownGroupLimit(queryType);

  const result: IBuilderBreakdownGroup[] = groupbys.map((groupby) => ({
    isActive: true,
    field: groupby.field,
    filter: groupby.predicate,
    binning: populateBinningFromBucketDef(groupby.bucketDef),
  }));

  if (result.length > limit) {
    return result.slice(0, limit);
  }

  return result;
};

/**
 * Parses Breakdown groups and segments to redux state
 */
export const parseBreakdownToStatePayload = (
  breakdown: MaybeNil<IBreakdownFieldsFragment>,
  queryType: QueryType,
): {
  builderBreakdown: IBuilderBreakdown;
  breakdownCohorts: ICohortRow[];
} => {
  if (!breakdown) {
    return {
      builderBreakdown: defaultBreakdown,
      breakdownCohorts: [],
    };
  }

  const { cohortRefs, groupbys, order, overall } = breakdown;

  const groups: IBuilderBreakdownGroup[] = parseBreakdownGroupbys(
    groupbys,
    queryType,
  );

  const breakdownCohorts = parseLimitedCohortRefsToBuilder(
    cohortRefs,
    MAX_BREAKDOWN_COHORTS,
  );

  return {
    builderBreakdown: {
      groups,
      order,
      overall,
    },
    breakdownCohorts,
  };
};

/**
 * In some places, we need to return any events when there is no event selected.
 * For example, user select filter first. Then we need to return any events.
 * Therefore, the default return value is any events.
 */
const generateEventsInputByEvents = (
  events: IEventConditionFieldsFragment['events'],
): IEventInput[] => {
  const virtualEvents = events.filter(byTypeName('VirtualEvent'));
  const anyTableEvents = events.filter(byTypeName('AnyTableEvent'));
  const basicEvents = events.filter(byTypeName('BasicEventValue'));
  if (virtualEvents?.length > 0) {
    return virtualEvents.map((virtualEvent) => ({
      kind: EventKind.Virtual,
      basicEventName: null,
      virtualEventId: virtualEvent.id,
      anyTableEventKey: null,
    }));
  }

  if (anyTableEvents?.length > 0 || basicEvents?.length > 0) {
    return [
      ...anyTableEvents.map((anyTableEvent) => ({
        kind: EventKind.AnyTable,
        basicEventName: null,
        virtualEventId: null,
        anyTableEventKey: anyTableEvent.key,
      })),
      ...basicEvents.map((basicEvent) => ({
        kind: EventKind.Basic,
        basicEventName: basicEvent.value.key,
        virtualEventId: null,
        anyTableEventKey: null,
      })),
    ];
  }

  return [defaultEventInput];
};

const generateEnumBucketsInput = (
  enumBuckets: IEnumBucketFieldsFragment[],
): IEnumBucketInput[] => {
  const filteredBuckets = enumBuckets.filter(
    (bucket) => bucket.values.length > 0,
  );

  return filteredBuckets.map(bucketWithCustomName).map(({ name, values }) => ({
    name,
    values: values.map((value) => value.key),
  }));
};

const generateRangeBucketsInput = (
  rangeBuckets: IRangeBucketFieldsFragment[],
): IRangeBucketInput[] => {
  const filteredBuckets = rangeBuckets.filter(
    (bucket) =>
      isNotNil(bucket.max) &&
      isNotNil(bucket.min) &&
      bucket.max >= 0 &&
      bucket.min >= 0,
  );

  return filteredBuckets
    .map(bucketWithCustomName)
    .map(({ max, min, name }) => ({
      min,
      max,
      name,
    }));
};

const defaultBasicBucketDefInput: IBasicBucketDefInput = Object.freeze({
  bucketsKind: BucketKind.Range,
  dateBuckets: null,
  enumBucketField: null,
  enumBucketSchemaId: null,
  enumBucketSplitOthers: null,
  enumBuckets: null,
  rangeBuckets: null,
});

export const generateBasicBucketDefInput = (
  binning: IBinning,
  schemaId: string,
  fieldKey: MaybeNil<string>,
): IBasicBucketDefInput => {
  if (binning.type === BucketDefType.Named) {
    const namedBucketDef = binning.namedBucketDef;
    const { kind, buckets, enumBucketField, enumBucketSplitOthers } =
      namedBucketDef;

    if (kind === BucketKind.Enum) {
      return {
        ...defaultBasicBucketDefInput,
        bucketsKind: kind,
        enumBucketField: enumBucketField?.key ? enumBucketField.key : '',
        enumBucketSchemaId: schemaId,
        enumBucketSplitOthers: enumBucketSplitOthers || false,
        enumBuckets: generateEnumBucketsInput(
          buckets.filter(narrowBucketsByKind(kind)),
        ),
      };
    }

    if (kind === BucketKind.Range) {
      return {
        ...defaultBasicBucketDefInput,
        bucketsKind: kind,
        rangeBuckets: generateRangeBucketsInput(
          buckets.filter(narrowBucketsByKind(kind)),
        ),
      };
    }

    // Date kind is not supported yet
    if (kind === BucketKind.Date) {
      return {
        ...defaultBasicBucketDefInput,
        bucketsKind: kind,
        dateBuckets: null,
      };
    }

    throw new UnreachableCaseError(kind);
  }

  const kind = binning.kind;

  // Date kind is not supported yet
  if (kind === BucketKind.Date) {
    return {
      ...defaultBasicBucketDefInput,
      bucketsKind: kind,
      dateBuckets: null,
    };
  }

  if (kind === BucketKind.Enum && binning.customBuckets.length > 0) {
    return {
      ...defaultBasicBucketDefInput,
      bucketsKind: kind,
      enumBucketField: fieldKey || null,
      enumBucketSchemaId: schemaId,
      enumBucketSplitOthers: binning.enumBucketSplitOthers || false,
      enumBuckets: generateEnumBucketsInput(binning.customBuckets),
    };
  }

  if (kind === BucketKind.Range) {
    if (binning.isAutoRangeBucket) {
      const rangeBuckets: IRangeBucketInput[] = getExpandedRangeBuckets(
        binning.autoRangeBucket,
      ).map(({ min, max, name }) => ({
        min,
        max,
        name,
      }));

      return {
        ...defaultBasicBucketDefInput,
        bucketsKind: kind,
        rangeBuckets,
      };
    }

    if (binning.customBuckets.length > 0) {
      return {
        ...defaultBasicBucketDefInput,
        bucketsKind: kind,
        rangeBuckets: generateRangeBucketsInput(binning.customBuckets),
      };
    }
  }

  return defaultBasicBucketDefInput;
};

export const generateBucketDefInput = (
  binning: Maybe<IBinning>,
  fieldKey: MaybeNil<string>,
  schemaId: string,
): InputMaybe<IBucketDefInput> => {
  if (!binning) {
    return null;
  }

  const type = binning.type;

  if (type === BucketDefType.Named && binning.namedBucketDef.id) {
    return {
      type,
      namedBucketDefInput: {
        bucketDefId: binning.namedBucketDef.id,
      },
      basicBucketDefInput: null,
    };
  }

  return {
    type,
    namedBucketDefInput: null,
    basicBucketDefInput: generateBasicBucketDefInput(
      binning,
      schemaId,
      fieldKey,
    ),
  };
};

export const populateBinningFromBucketDef = (
  bucketDef: Maybe<IBucketDefFieldsFragment>,
): Maybe<IBinning> => {
  if (!bucketDef) {
    return null;
  }
  if (narrowBucketDefByType(bucketDef, BucketDefType.Named)) {
    return {
      type: BucketDefType.Named,
      kind: bucketDef.kind,
      namedBucketDef: bucketDef,
      updateKey: 0,
    } satisfies INamedBinning;
  } else if (narrowBucketDefByType(bucketDef, BucketDefType.Basic)) {
    let binning: IBasicBinning;
    switch (bucketDef.kind) {
      case BucketKind.Date: {
        binning = {
          type: BucketDefType.Basic,
          kind: BucketKind.Date,
          updateKey: 0,
          customBuckets: bucketDef.buckets.filter(
            narrowBucketsByKind(bucketDef.kind),
          ),
        } satisfies IDateBinning;
        break;
      }
      case BucketKind.Range: {
        binning = {
          type: BucketDefType.Basic,
          kind: BucketKind.Range,
          updateKey: 0,
          customBuckets: bucketDef.buckets
            .filter(narrowBucketsByKind(bucketDef.kind))
            .map(bucketWithCustomName),
          isAutoRangeBucket: false,
        } satisfies IRangeBinning;
        break;
      }
      case BucketKind.Enum: {
        binning = {
          type: BucketDefType.Basic,
          kind: BucketKind.Enum,
          updateKey: 0,
          customBuckets: bucketDef.buckets
            .filter(narrowBucketsByKind(bucketDef.kind))
            .map(bucketWithCustomName),
          enumBucketSplitOthers: bucketDef.enumBucketSplitOthers ?? false,
        } satisfies IEnumBinning;
        break;
      }
      default:
        throw new UnreachableCaseError(bucketDef.kind);
    }
    return binning;
  } else {
    throw new UnreachableCaseError(bucketDef);
  }
};

/**
 * parse the single eventCondition from redux to input format.
 * @param eventCondition unparsed event condition
 * @param schemaId
 * @param hasPropertyField true if in measure.
 * @returns input for event condition
 */
const parseEventConditionToInput = (
  eventCondition: IEventConditionFieldsFragment,
  schemaId: string,
  hasPropertyField?: boolean,
): IEventConditionInput => {
  const { events, field, filter } = eventCondition;

  return {
    events: generateEventsInputByEvents(events),
    filter: filter ? parseFilterToInput(filter, schemaId) : null,
    field: hasPropertyField && field ? field.key : null,
  };
};

export const populateFunnelState = (
  dispatch: AppDispatch,
  builderState: IPopulateFunnelBuilder,
) => {
  dispatch(populateReportData(builderState));
};

/**
 * These constants depend on Kobol's configuration
 * Should be in-sync with co.kubit.kobol.analysis.Slice class in Kobol
 */
const FALSE_FIELD_VALUE_NAME = 'False';

const EMPTY_SLICE_VALUE = '<Empty>';
const NULL_SLICE_VALUE = 'N/A';

/**
 * A template for a global filter. Use Immer's produce to modify it
 */
const defaultGlobalFilter = freeze<IFilterFieldsFragment>(
  {
    __typename: 'BasicFilter',
    type: FilterType.Basic,
    conditions: [],
    logicOps: [],
  },
  true,
);

/**
 * Helper function that creates a IBasicFieldValue from a constant value
 * @param value
 */
const createBasicFieldValue = (
  value: string,
): IBasicFieldValueFieldsFragment => ({
  __typename: 'BasicFieldValue',
  key: value,
  name: value,
  description: null,
  isDisabled: false,
});

/**
 * Helper function that creates a IBasicEventValue from a constant value
 * @param name
 */
const createBasicEventValue = (
  name: string,
): IBasicEventValueFieldsFragment => ({
  __typename: 'BasicEventValue',
  value: {
    __typename: 'BasicFieldValue',
    key: name,
    name,
    description: null,
    isDisabled: false,
  },
  categories: [],
  kind: EventKind.Basic,
  isFavorite: false,
  isManaged: false,
  tableNames: [],
});

const findCohortInBreakdown = (
  breakdown: MaybeNil<IBreakdownFieldsFragment>,
  sliceUserAlias: ISliceUserAliasFieldsFragment,
): MaybeNil<ICohortRefFieldsFragment> => {
  if (
    isNil(breakdown) ||
    sliceUserAlias.__typename !== 'UserAliasSliceBreakdown'
  ) {
    return null;
  }

  const groupBysAliases = new Set(sliceUserAlias.groupBys);

  const cohortRef = breakdown.cohortRefs.find((cohortRef) =>
    groupBysAliases.has(cohortRef.cohort.userAlias.aliasId),
  );

  if (cohortRef) {
    return cohortRef;
  }

  return null;
};

/**
 * Appends global filter by cohort from a breakdown cohort
 * Cohorts is matched based on the Alias ID
 */
function appendGlobalCohortFilterFromBreakdown(
  breakdown: MaybeNil<IBreakdownFieldsFragment>,
  cohortRefs: ICohortRefFieldsFragment[],
  sliceUserAlias: ISliceUserAliasFieldsFragment,
): ICohortRefFieldsFragment[] {
  const cohortInBreakdown = findCohortInBreakdown(breakdown, sliceUserAlias);

  if (cohortInBreakdown) {
    return produce(cohortRefs, (draft) => {
      draft.push(cohortInBreakdown);
    });
  }

  return cohortRefs;
}

/**
 * Adds additional global filter based on selected slice values
 * Breakdowns are matched by name from the slice's hint. Overall slice
 * does not add global filter
 *
 * @param breakdown
 * @param pathGlobalFieldsFilter
 * @param sliceUserAlias
 * @param sliceHints
 * @param andLogicOp
 */
const appendGlobalFieldsFilterFromBreakdown = (
  breakdown: MaybeNil<IBreakdownFieldsFragment>,
  pathGlobalFieldsFilter: MaybeNil<IFilterFieldsFragment>,
  sliceUserAlias: ISliceUserAliasFieldsFragment,
  sliceHints: ISliceNameHint[],
  andLogicOp: ILogicalOperator,
): MaybeNil<IFilterFieldsFragment> => {
  if (
    isNil(breakdown) ||
    sliceUserAlias.__typename !== 'UserAliasSliceBreakdown' ||
    (!isNil(pathGlobalFieldsFilter) &&
      pathGlobalFieldsFilter.type === FilterType.Named)
  ) {
    return pathGlobalFieldsFilter;
  }

  const funnelGlobalFilter = pathGlobalFieldsFilter ?? defaultGlobalFilter;
  assertCondition(funnelGlobalFilter.type === FilterType.Basic);

  const conditionFromBreakdown: IConditionFieldsFragment = {
    __typename: 'Condition',
    logicOps: [],
    predicates: [],
  };

  for (const sliceHint of sliceHints) {
    const breakdownField = breakdown.groupbys.find(
      (gb) => gb.field.name === sliceHint.name,
    );

    if (!breakdownField) {
      continue;
    }

    // "N/A" slices generate "<Breakdown Field> exists False" predicate
    // "<Empty>" slices generate <Breakdown Field> = "" predicate
    // other - generate "<Breakdown Field> = <Slice Value>" predicate
    // This is how Kobol generates global filter in
    // co.kubit.kobol.analysis.FormulaEditor#formulaBracketCondGlobalFilter
    let operand: IBasicFieldValueFieldsFragment,
      relOp: IRelationalOperatorFieldsFragment;
    if (sliceHint.value === NULL_SLICE_VALUE) {
      const existsRelOp = breakdownField.field.relOps.find(
        (op) => op.key === RelationalOperatorKey.Exists,
      );
      assertCondition(!isNil(existsRelOp));
      relOp = existsRelOp;

      const falseOperand = existsRelOp.values.find(
        (value) => value === FALSE_FIELD_VALUE_NAME,
      );
      assertCondition(!isNil(falseOperand));
      operand = createBasicFieldValue(falseOperand);
    } else {
      const equalRelOp = breakdownField.field.relOps.find(
        (op) => op.key === RelationalOperatorKey.Equal,
      );
      assertCondition(!isNil(equalRelOp));
      relOp = equalRelOp;
      operand = createBasicFieldValue(
        sliceHint.value === EMPTY_SLICE_VALUE ? '' : sliceHint.value,
      );
    }

    conditionFromBreakdown.predicates.push({
      __typename: 'SimplePredicate',
      field: breakdownField.field,
      relOp,
      matchCase: true,
      operands: {
        __typename: 'ValueOperands',
        values: [operand],
      },
    });
  }

  // if we generate a new condition, append it to the existing (if any)
  // global filter
  if (conditionFromBreakdown.predicates.length > 0) {
    // logic ops are inbetween predicates, thus 1 less than predicates
    for (let i = 0; i < conditionFromBreakdown.predicates.length - 1; i++) {
      conditionFromBreakdown.logicOps.push(andLogicOp);
    }

    return produce(funnelGlobalFilter, (draft) => {
      if (draft.conditions.length > 0) {
        draft.logicOps.push(andLogicOp);
      }
      draft.conditions.push(conditionFromBreakdown);
    });
  } else {
    // no new conditions are generated, return the original filter
    return pathGlobalFieldsFilter;
  }
};

export const createFunnelFromPath = (
  pathSteps: string[][],
  sliceUserAlias: ISliceUserAliasFieldsFragment,
  sliceHints: ISliceNameHint[],
  {
    direction,
    breakdown,
    filter: pathGlobalFieldsFilter,
    groupby,
    partitions,
    samplePercentage,
    schema,
    startConditions,
    endConditions,
    subject,
    cohortRefs,
  }: IPathQueryFieldsFragment,
  {
    end,
    start,
    datePicker,
  }: Pick<IPathAnalysis, 'start' | 'end' | 'datePicker'>,
  andLogicOp: ILogicalOperator,
): IPopulateFunnelBuilder => {
  const conditions =
    direction.key === PathDirectionKey.Forward
      ? startConditions
      : endConditions;
  const funnelStartOrEndStep: IStep = {
    ...defaultStep(),
    eventConditions: conditions,
    name: null,
  };
  // skip the first step from the highlighted path
  const remainingPathStep = pathSteps.length > 0 ? pathSteps.slice(1) : [];
  const remainingFunnelSteps: IStep[] = remainingPathStep.map((eventNames) => {
    return {
      ...defaultStep(),
      eventConditions: [
        {
          events: eventNames.map(createBasicEventValue),
          filter: null,
          field: null,
          __typename: 'EventCondition',
        },
      ],
      name: null,
    };
  });
  const allBuilderFunnelSteps = [funnelStartOrEndStep, ...remainingFunnelSteps];
  // backward path displays events in End -> Start direction. The highlighted path for backward
  // path should be reversed to create a forward funnel (left-most path step is the end step of the funnel)
  if (direction.key === PathDirectionKey.Backward) {
    allBuilderFunnelSteps.reverse();
  }

  const funnelGlobalCohortsFilter = appendGlobalCohortFilterFromBreakdown(
    breakdown,
    cohortRefs,
    sliceUserAlias,
  );

  const funnelGlobalFieldsFilter = appendGlobalFieldsFilterFromBreakdown(
    breakdown,
    pathGlobalFieldsFilter,
    sliceUserAlias,
    sliceHints,
    andLogicOp,
  );

  const queryType = QueryType.Funnel;

  const { builderBreakdown, breakdownCohorts } = parseBreakdownToStatePayload(
    breakdown,
    queryType,
  );

  const builderState: IPopulateFunnelBuilder = {
    ...defaultReportFilters,
    queryType,
    steps: allBuilderFunnelSteps,
    stepOrders: [], // TODO
    activeSchemaId: schema.id,
    sourceId: null,
    timeWindow: null,
    selectedOptions: {
      type: queryType,
      funnelOrder: FunnelOrderKey.Exact,
      dateKind: DateKindKey.Event,
      funnelCustomOrder: FunnelOrderKey.Exact,
      mode: FunnelModeKey.Conversion,
      partitionValues: partitions,
      groupby: groupby?.key,
      subject: subject?.key,
      timeUnit: TimeUnit.AllTime,
    },
    breakdownCohorts,
    globalCohorts: parseGlobalCohortsToStatePayload(funnelGlobalCohortsFilter),
    additionalSettings: [],
    breakdown: builderBreakdown,
    dateInput: {
      startDate: start,
      endDate: end,
      datePicker,
    },
  };

  if (samplePercentage) {
    builderState.formulaSamplingRate = samplePercentage;
  }

  if (funnelGlobalFieldsFilter) {
    builderState.filter = funnelGlobalFieldsFilter;
  }

  return builderState;
};

type BucketKindToTypeName = {
  [BucketKind.Date]: IDateBucketFieldsFragment;
  [BucketKind.Enum]: IEnumBucketFieldsFragment;
  [BucketKind.Range]: IRangeBucketFieldsFragment;
};

export const narrowBucketsByKind =
  <
    T extends IBucketFieldsFragment,
    K extends keyof BucketKindToTypeName,
    R extends T & BucketKindToTypeName[K],
  >(
    kind: K,
  ) =>
  (bucket: T): bucket is R =>
    bucket.kind === kind;

type BucketTypeDefToTypeName = {
  [BucketDefType.Named]: INamedBucketDefFieldsFragment;
  [BucketDefType.Basic]: IBasicBucketDefFieldsFragment;
};

export const narrowBucketDefByType = <
  T extends IBucketDefFieldsFragment,
  K extends keyof BucketTypeDefToTypeName,
  R extends T & BucketTypeDefToTypeName[K],
>(
  bucketDef: T,
  type: K,
): bucketDef is R => bucketDef.type === type;

export const bucketWithCustomName = <
  T extends { name: string; isCustomName: MaybeNil<boolean> },
>(
  bucket: T,
): T => {
  return {
    ...bucket,
    name: bucket.isCustomName ? bucket.name : '',
  };
};

export const findMeasureIdByMeasureKeyInBuilderInfo = (
  expressionInfo: IExpressionQueryFieldsFragment,
  measureKey: string,
) => {
  const measure = expressionInfo.measure;

  // Histogram only has one result measure, treat as special case
  // no need to find new id and always use the first measure in new analysis
  if (expressionInfo.expressionMode.key === ExpressionModeKey.Histogram) {
    return measure.id;
  }

  let measureId;
  if (measure.__typename === 'SimpleMeasure') {
    measureId = measure.id;
  } else if (measure.__typename === 'CompoundMeasure') {
    const measures = [measure, ...measure.measures];
    const foundMeasure = measures.find((m) => m.key === measureKey);
    assertNotNil(foundMeasure);
    measureId = foundMeasure.id;
  } else {
    throw new UnreachableCaseError(measure);
  }
  return measureId;
};

const defaultDatePickerInput = freeze<Omit<IDatePickerInput, 'type'>>({
  fixed: null,
  last: null,
  lastFull: null,
  next: null,
  since: null,
  startOf: null,
});

export const toDatePickerInput = (datePicker: IDatePickerFieldsFragment) => {
  const typename = datePicker.__typename;
  if (typename === 'DatePickerFixed') {
    return {
      ...defaultDatePickerInput,
      type: DatePickerType.Fixed,
      fixed: {
        start: datePicker.start,
        end: datePicker.end,
      },
    };
  } else if (typename === 'DatePickerLast') {
    return {
      ...defaultDatePickerInput,
      type: DatePickerType.Last,
      last: {
        period: datePicker.period,
        unit: datePicker.unit,
      },
    };
  } else if (typename === 'DatePickerLastFull') {
    return {
      ...defaultDatePickerInput,
      type: DatePickerType.LastFull,
      lastFull: {
        period: datePicker.period,
        unit: datePicker.unit,
      },
    };
  } else if (typename === 'DatePickerSince') {
    return {
      ...defaultDatePickerInput,
      type: DatePickerType.Since,
      since: {
        start: datePicker.start,
      },
    };
  } else if (typename === 'DatePickerStartOf') {
    return {
      ...defaultDatePickerInput,
      type: DatePickerType.StartOf,
      startOf: {
        unit: datePicker.unit,
      },
    };
  } else {
    throw new UnreachableCaseError(typename);
  }
};

export {
  parseGlobalCohortsToStatePayload,
  generateEventsInputByEvents,
  parseEventConditionToInput,
};
